import {makeAutoObservable, reaction, runInAction} from 'mobx'
import Agent from '../../api/Agent'
import {ConsumerAttachmentFormValues, IConsumerAttachment} from '../../models/Consumer/ConsumerAttachment'
import {toast} from 'react-toastify'
import {RootStore} from '../RootStore'
import dayjs from 'dayjs'


export default class ConsumerAttachmentStore {
    rootStore: RootStore

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore
        makeAutoObservable(this)

        reaction(
            () => this.predicate.keys(),
            () => {
                this.page = 0
                this.consumerAttachmentRegistry.clear()
                this.loadConsumerAttachments()
            }
        )

        reaction(
            () => this.currentConsumerId,
            () => {
                this.page = 0
                this.consumerAttachmentRegistry.clear()
                !!this.currentConsumerId ? this.getConsumerAttachmentsByConsumerId() : this.loadConsumerAttachments()
            }
        )
    }

    consumerAttachmentRegistry = new Map()
    consumerAttachment: IConsumerAttachment | null = null
    editMode = false


    currentConsumerId = '';
    consumerAttachmentCount = 0
    page = 0
    limit = 5

    predicate = new Map()

    setCurrentConsumerId = (consumerId: string) => {
        this.currentConsumerId = consumerId;
    }

    setPredicate = (predicate?: string, value?: any) => {
        if (!predicate || predicate === '') {
            this.predicate.clear()
        } else {
            this.predicate.delete(predicate)
            if (!!value)
                this.predicate.set(predicate, value instanceof Date ? new Date(value).toISOString() : value)
        }
    }

    get axiosParams() {
        const params = new URLSearchParams()
        params.append('limit', String(this.limit))
        params.append('offset', `${this.page ? this.page * this.limit : 0}`)
        this.predicate.forEach((value, key) => {
            if (!!value) params.append(key, value)
        })
        return params
    }

    get totalPages() {
        return Math.ceil(this.consumerAttachmentCount / this.limit)
    }

    get consumerAttachmentsById() {
        return Array.from(this.consumerAttachmentRegistry.values()).sort((a, b) => b.id - a.id)
    }

    get consumerAttachmentsByDate() {
        return Array.from(this.consumerAttachmentRegistry.values()).sort(
            (a, b) => dayjs(b.createdOn).isBefore(a.createdOn) ? -1 : 1//   Date.parse(a.SubmittedOn)-Date.parse(b.SubmittedOn)
        )
    }

    getConsumerAttachment = (id: string) => {
        return this.consumerAttachmentRegistry.get(id)
    }

    setPage = (page: number) => {
        this.page = page
    }

    setLimit = (limit: number) => {
        this.limit = limit
    }

    handleChangePage = (newPage: number, newRow: number) => {
        //sets page, we do -1 because the offset will be multiplied against 
        this.setPage(newPage - 1)
        //sets the row limit
        this.setLimit(newRow)
        //clears all records in the registry so that next page didnt load the prev records
        this.consumerAttachmentRegistry.clear()
        this.loadConsumerAttachments()
    }

    clearConsumerAttachment = () => {
        this.consumerAttachmentRegistry.clear()
    }

    loadConsumerAttachments = async () => {
        let loadingId = this.rootStore.commonStore.setIsLoading()

        try {
            if (!!this.currentConsumerId) {
                const consumerAttachmentsEnvelope = await Agent.ConsumerAttachments.list(this.axiosParams);
                const {modelsDto, modelCount} = consumerAttachmentsEnvelope;

                runInAction(() => {
                    this.clearConsumerAttachment();
                    modelsDto.forEach((consumerAttachment: IConsumerAttachment) => {
                        this.consumerAttachmentRegistry.set(consumerAttachment.id, consumerAttachment)
                    })
                    this.consumerAttachmentCount = modelCount
                })
            }
        } catch (error) {
            throw error
        }
        runInAction(() => {
            this.rootStore.commonStore.setIsLoading(loadingId)
        })
    }

    getConsumerAttachmentById = async (id: string) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        try {
            let consumerAttachment = await Agent.ConsumerAttachments.byId(id)
            runInAction(() => {
                this.consumerAttachment = consumerAttachment
                this.consumerAttachmentRegistry.set(consumerAttachment.id, consumerAttachment)
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
            return consumerAttachment
        } catch (error) {
            this.rootStore.errorStore.addError(error)
        }
        runInAction(() => {
            this.rootStore.commonStore.setIsLoading(loadingId)
        })
    }

    getConsumerAttachmentsByConsumerId = async (consumerId?: string) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        try {
            let consumerAttachmentEnvelope = await Agent.ConsumerAttachments.byConsumerId(consumerId || this.currentConsumerId, this.axiosParams)
            const {modelsDto, modelCount} = consumerAttachmentEnvelope
            runInAction(() => {
                modelsDto.forEach(consumerAttachment => {
                    this.consumerAttachmentRegistry.set(consumerAttachment.id, consumerAttachment)
                })

                this.consumerAttachmentCount = modelCount
            })
        } catch (error) {
            this.rootStore.errorStore.addError(error)
        } finally {
            runInAction(() => {
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
        }
    }

    createConsumerAttachment = async (consumerAttachment: ConsumerAttachmentFormValues) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        try {
            const newAttachment = await Agent.ConsumerAttachments.create(consumerAttachment)
            await this.loadConsumerAttachments()
            runInAction(() => {
                this.consumerAttachmentRegistry.set(newAttachment.id, newAttachment)
                this.rootStore.modalStore.closeModal('AttachmentModal')
                this.rootStore.commonStore.setIsLoading(loadingId)
                this.rootStore.modalStore.closeModal('AttachmentModal')
            })

            toast.success('Saved')
        } catch (error) {
            this.rootStore.errorStore.addError(error)
        } finally {
            runInAction(() => {
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
        }
    }

    deleteConsumerAttachment = async (id: string) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        try {
            await Agent.ConsumerAttachments.delete(id)
            runInAction(() => {
                this.consumerAttachmentRegistry.delete(id)
                this.rootStore.commonStore.setIsLoading(loadingId)
                this.rootStore.modalStore.closeModal('ConsumerAttachmentStore')
            })
            toast.success('Deleted!')
        } catch (error) {
            this.rootStore.errorStore.addError(error)
        } finally {
            runInAction(() => {
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
        }
    }

}
